/**
 * 🔄 Shared Document Reset Utility
 * 
 * Creates a fresh, normalized shared document structure for all users
 * to ensure proper IPFS link sharing and cross-user visibility.
 */

import { getYjsInstance } from './yjsLoader.js'

/**
 * Reset and normalize the shared public archive document
 * This ensures all users connect to the same clean document
 */
export async function resetSharedDocument(options = {}) {
  const {
    preserveUserData = true,
    forceReset = false,
    onProgress = () => {}
  } = options

  console.log('🔄 Starting shared document reset...', { preserveUserData, forceReset })

  try {
    onProgress({ step: 1, message: 'Loading Yjs dependencies...', progress: 0 })
    
    const { Y, IndexeddbPersistence } = await getYjsInstance()

    onProgress({ step: 2, message: 'Clearing old shared document...', progress: 20 })

    // Clear the shared public archive document completely
    const publicDocId = 'etherith-public-archive'
    await clearIndexedDBDoc(publicDocId)
    
    onProgress({ step: 3, message: 'Creating fresh shared document...', progress: 40 })

    // Create a fresh shared document
    const freshPublicDoc = new Y.Doc()
    const freshArchiveArray = freshPublicDoc.getArray('publicArchive')

    // Set up persistence for the fresh document
    const publicProvider = new IndexeddbPersistence(publicDocId, freshPublicDoc)
    
    // Wait for initial sync
    await waitForSync(publicProvider)

    onProgress({ step: 4, message: 'Initializing document structure...', progress: 60 })

    // Add initial document metadata
    const metadataMap = freshPublicDoc.getMap('metadata')
    metadataMap.set('version', '2.0')
    metadataMap.set('created', new Date().toISOString())
    metadataMap.set('resetReason', 'Normalized shared document structure')
    metadataMap.set('structure', {
      publicArchive: 'Y.Array containing all public memories with IPFS CIDs',
      metadata: 'Y.Map containing document information'
    })

    onProgress({ step: 5, message: 'Verifying document structure...', progress: 80 })

    // Verify the structure
    const verification = {
      documentId: publicDocId,
      arrayExists: !!freshArchiveArray,
      arrayLength: freshArchiveArray.length,
      metadataExists: !!metadataMap,
      version: metadataMap.get('version'),
      clientId: freshPublicDoc.clientID
    }

    console.log('✅ Fresh shared document created:', verification)

    onProgress({ step: 6, message: 'Reset complete!', progress: 100 })

    // Clean up
    publicProvider.destroy()

    return {
      success: true,
      documentId: publicDocId,
      verification,
      message: 'Shared document reset successfully. All users will now connect to the same clean document.'
    }

  } catch (error) {
    console.error('❌ Shared document reset failed:', error)
    onProgress({ step: -1, message: `Reset failed: ${error.message}`, progress: -1 })
    throw error
  }
}

/**
 * Clear specific IndexedDB document
 */
function clearIndexedDBDoc(docName) {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !window.indexedDB) {
      console.warn('IndexedDB not available, skipping clear')
      resolve()
      return
    }

    console.log(`🗑️ Clearing IndexedDB document: ${docName}`)
    
    const request = indexedDB.deleteDatabase(`yjs-indexeddb-${docName}`)
    
    request.onsuccess = () => {
      console.log(`✅ Cleared IndexedDB document: ${docName}`)
      resolve()
    }
    
    request.onerror = () => {
      console.error(`❌ Failed to clear IndexedDB document: ${docName}`, request.error)
      reject(request.error)
    }
    
    request.onblocked = () => {
      console.warn(`⚠️ IndexedDB delete blocked for: ${docName}`)
      // Fallback timeout
      setTimeout(resolve, 3000)
    }
    
    // Fallback timeout
    setTimeout(() => {
      console.log(`⏰ Clear timeout for: ${docName}`)
      resolve()
    }, 5000)
  })
}

/**
 * Wait for IndexedDB provider to sync
 */
function waitForSync(provider) {
  return new Promise((resolve) => {
    const onSynced = () => {
      provider.off('synced', onSynced)
      resolve()
    }
    
    if (provider.synced) {
      resolve()
    } else {
      provider.on('synced', onSynced)
      // Fallback timeout
      setTimeout(resolve, 3000)
    }
  })
}

/**
 * Get shared document status and statistics
 */
export async function getSharedDocumentStatus() {
  try {
    const { Y, IndexeddbPersistence } = await getYjsInstance()
    
    const publicDocId = 'etherith-public-archive'
    const publicDoc = new Y.Doc()
    const provider = new IndexeddbPersistence(publicDocId, publicDoc)
    
    await waitForSync(provider)
    
    const archiveArray = publicDoc.getArray('publicArchive')
    const metadataMap = publicDoc.getMap('metadata')
    const memories = archiveArray.toArray()
    
    // Analyze memory statistics
    const stats = {
      documentId: publicDocId,
      totalMemories: memories.length,
      memoriesWithIPFS: memories.filter(m => m.cid || m.localCid).length,
      memoriesWithoutIPFS: memories.filter(m => !m.cid && !m.localCid).length,
      uniqueUsers: [...new Set(memories.map(m => m.userId))].length,
      version: metadataMap.get('version'),
      created: metadataMap.get('created'),
      lastUpdated: memories.length > 0 ? new Date(Math.max(...memories.map(m => m.timestamp || 0))).toISOString() : null,
      clientId: publicDoc.clientID
    }
    
    console.log('📊 Shared document status:', stats)
    
    provider.destroy()
    
    return stats
    
  } catch (error) {
    console.error('❌ Failed to get shared document status:', error)
    throw error
  }
}

/**
 * Migrate existing memories to fresh shared document
 * Use this if you want to preserve existing public memories
 */
export async function migrateToFreshDocument(walletAddress, options = {}) {
  const {
    onProgress = () => {}
  } = options

  console.log('🚚 Starting migration to fresh document...', { walletAddress })

  try {
    onProgress({ step: 1, message: 'Backing up existing memories...', progress: 0 })

    // Get current shared document status
    const currentStatus = await getSharedDocumentStatus()
    console.log('📋 Current document status:', currentStatus)

    if (currentStatus.totalMemories === 0) {
      console.log('📝 No memories to migrate, proceeding with clean reset')
      return await resetSharedDocument({ ...options, preserveUserData: false })
    }

    // TODO: Add actual migration logic here if needed
    // For now, we'll just do a clean reset
    onProgress({ step: 2, message: 'Creating fresh document...', progress: 50 })
    
    const result = await resetSharedDocument(options)
    
    onProgress({ step: 3, message: 'Migration complete!', progress: 100 })
    
    return {
      ...result,
      migrated: true,
      previousStats: currentStatus
    }

  } catch (error) {
    console.error('❌ Migration to fresh document failed:', error)
    throw error
  }
}
